import { type RefObject, useEffect, useRef, useState } from "react"

declare global {
  interface Window {
    grecaptcha?: {
      render: (
        container: string | HTMLElement,
        params: {
          sitekey: string
          callback?: (token: string) => void
          "expired-callback"?: () => void
        },
      ) => number
      reset: (widgetId?: number) => void
      getResponse: (widgetId?: number) => string
      ready: (cb: () => void) => void
    }
  }
}

interface UseRecaptchaOptions {
  enabled?: boolean
}

interface UseRecaptchaReturn {
  ref: RefObject<HTMLDivElement>
  isPassed: boolean
  getToken: () => string
  reset: () => void
}

// Container passed by ref (not string id) so React owns the DOM lifecycle and SSR
// doesn't need to generate matching ids. The "already rendered" fix lives in the
// cleanup below — releasing Google's internal client entry, not the ref itself.
const useRecaptcha = ({ enabled = true }: UseRecaptchaOptions = {}): UseRecaptchaReturn => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const [isPassed, setIsPassed] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined" || !enabled) {
      return undefined
    }

    const tryRender = (): boolean => {
      if (widgetIdRef.current !== null) return true
      if (!window.grecaptcha?.render) return false
      if (!containerRef.current) return false
      widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
        sitekey: process.env.NEXT_PUBLIC_GR_SITE_KEY as string,
        callback: () => setIsPassed(true),
        "expired-callback": () => setIsPassed(false),
      })
      return true
    }

    let intervalId: number | null = null
    if (!tryRender()) {
      intervalId = window.setInterval(() => {
        if (tryRender() && intervalId !== null) {
          window.clearInterval(intervalId)
        }
      }, 200)
    }

    return () => {
      if (intervalId !== null) window.clearInterval(intervalId)
      if (widgetIdRef.current !== null) {
        window.grecaptcha?.reset?.(widgetIdRef.current)
        // reset() clears the response but leaves the client entry in ___grecaptcha_cfg.clients.
        // Without this delete, re-render into the same element (Strict Mode double-invoke,
        // multi-step re-entry, browser back-nav) throws "already rendered".
        // eslint-disable-next-line no-underscore-dangle
        const cfg = (window as unknown as { ___grecaptcha_cfg?: { clients?: Record<number, unknown> } }).___grecaptcha_cfg
        if (cfg?.clients) delete cfg.clients[widgetIdRef.current]
        widgetIdRef.current = null
      }
      // Defensive iframe cleanup — no-op if React already detached the node.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      containerRef.current?.replaceChildren()
      setIsPassed(false)
    }
  }, [enabled])

  return {
    ref: containerRef,
    isPassed,
    getToken: () =>
      widgetIdRef.current !== null
        ? window.grecaptcha?.getResponse?.(widgetIdRef.current) || ""
        : "",
    reset: () => {
      if (widgetIdRef.current !== null) window.grecaptcha?.reset?.(widgetIdRef.current)
      setIsPassed(false)
    },
  }
}

export default useRecaptcha
